import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PageHeader from "../../components/layout/PageHeader";
import { reportsApi } from "../../api/reports";
import type { TrendData, AcDifficultyRankings } from "../../types";
import { useSeason } from "../../context/SeasonContext";

const TERM_COLORS = ["#166534", "#92400e", "#1e40af", "#7c3aed", "#be123c"];

function extractTermLabel(name: string, position: number): string {
  const ordinalMatch = name.match(/(\d+(?:st|nd|rd|th))\s+[Tt]erm/i);
  if (ordinalMatch) return `${ordinalMatch[1]} Term`;
  if (/\bfirst\s+term\b/i.test(name)) return "1st Term";
  if (/\bsecond\s+term\b/i.test(name)) return "2nd Term";
  if (/\bthird\s+term\b/i.test(name)) return "3rd Term";
  if (/\bfourth\s+term\b/i.test(name)) return "4th Term";
  if (/\bfull\b/i.test(name)) return "Full Term";
  return `Term ${position}`;
}
const CABIN_COLORS = [
  "#166534", "#92400e", "#1e40af", "#7c3aed", "#be123c",
  "#0f766e", "#b45309", "#1d4ed8", "#6d28d9", "#9f1239",
  "#15803d", "#a16207", "#1e3a8a", "#5b21b6", "#881337",
  "#16a34a", "#ca8a04", "#2563eb", "#7c3aed", "#e11d48",
];

type TabId = "enrollment" | "acs" | "difficulty";

const TOP_N = 25;
const MIN_SAMPLE = 5;

interface ChartPoint {
  year: number;
  [key: string]: number | string;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h3 className="text-base font-semibold text-stone-800 mb-4 mt-8 first:mt-0">{title}</h3>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="camp-card p-6 mb-6">
      <h4 className="text-sm font-semibold text-stone-700 mb-4">{title}</h4>
      {children}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex items-center justify-center h-48 text-stone-400 text-sm">
      No data available
    </div>
  );
}

export default function TrendReporting() {
  const [data, setData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("enrollment");
  const { selectedYear, selectedTermId, selectedTerm } = useSeason();

  const [lifetimeRankings, setLifetimeRankings] = useState<AcDifficultyRankings | null>(null);
  const [termRankings, setTermRankings] = useState<AcDifficultyRankings | null>(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  useEffect(() => {
    reportsApi.trends()
      .then(setData)
      .catch((err) => setError(err?.response?.data?.error ?? "Failed to load trend data"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab !== "difficulty") return;
    setRankingsLoading(true);
    Promise.all([
      reportsApi.acDifficultyRankings({ scope: "lifetime" }),
      reportsApi.acDifficultyRankings({ scope: "term", termId: selectedTermId, year: selectedYear }),
    ])
      .then(([lifetime, term]) => {
        setLifetimeRankings(lifetime);
        setTermRankings(term);
      })
      .catch((err) => setError(err?.response?.data?.error ?? "Failed to load rankings"))
      .finally(() => setRankingsLoading(false));
  }, [activeTab, selectedTermId, selectedYear]);

  if (loading) {
    return (
      <div>
        <PageHeader title="Trend Reporting" />
        <div className="p-6 lg:p-8 flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <PageHeader title="Trend Reporting" />
        <div className="p-6 lg:p-8">
          <p className="text-red-600">{error ?? "No data"}</p>
        </div>
      </div>
    );
  }

  const hasData = data.years.length > 0;

  // ── Enrollment: All campers line chart data ─────────────────────────────
  const enrollmentAllData: ChartPoint[] = data.enrollment.all.map((p) => ({
    year: p.year,
    "All Campers": p.count,
  }));

  // Build unique term labels: extract short name (e.g. "1st Term") from the most recent year's bunk plan name
  const termLabels = data.enrollment.byTerm.map((t) => {
    const last = t.data[t.data.length - 1];
    return extractTermLabel(last?.termName ?? "", t.termPosition);
  });

  // Merge term enrollment into one dataset per year, keyed consistently by termLabels index
  const termEnrollmentData: ChartPoint[] = data.years.map((year) => {
    const point: ChartPoint = { year };
    data.enrollment.byTerm.forEach((term, idx) => {
      const entry = term.data.find((d) => d.year === year);
      if (entry !== undefined) point[termLabels[idx]] = entry.count;
    });
    return point;
  });

  const divisionEnrollmentLabels = data.enrollment.byDivision.map((d) => d.divisionName);

  // ── AC trends: merge all years into chart points ────────────────────────
  type AcMetricKey = "totalACs" | "acsPerCamper" | "totalHonors" | "honorsPerCamper" | "totalGraduations" | "graduationsPerCamper";

  function buildAcAllData(metric: AcMetricKey): ChartPoint[] {
    return data!.acTrends.all.map((y) => ({
      year: y.year,
      "All Campers": y[metric],
    }));
  }

  function buildAcTermData(metric: AcMetricKey): { points: ChartPoint[]; labels: string[] } {
    const termLabelsForAc = data!.acTrends.byTerm.map((t) => {
      const last = t.data[t.data.length - 1];
      return extractTermLabel(last?.termName ?? "", t.termPosition);
    });
    const points: ChartPoint[] = data!.years.map((year) => {
      const pt: ChartPoint = { year };
      data!.acTrends.byTerm.forEach((term, idx) => {
        const entry = term.data.find((d) => d.year === year);
        const label = termLabelsForAc[idx];
        pt[label] = entry ? entry[metric] : 0;
      });
      return pt;
    });
    return { points, labels: termLabelsForAc };
  }

  function buildAcDivisionData(metric: AcMetricKey): { points: ChartPoint[]; labels: string[] } {
    const labels = data!.acTrends.byDivision.map((d) => d.divisionName);
    const points: ChartPoint[] = data!.years.map((year) => {
      const pt: ChartPoint = { year };
      data!.acTrends.byDivision.forEach((div) => {
        const entry = div.data.find((d) => d.year === year);
        pt[div.divisionName] = entry ? entry[metric] : 0;
      });
      return pt;
    });
    return { points, labels };
  }

  const acTermLabels = data.acTrends.byTerm.map((t) => {
    const last = t.data[t.data.length - 1];
    return extractTermLabel(last?.termName ?? "", t.termPosition);
  });

  const acDivisionLabels = data.acTrends.byDivision.map((d) => d.divisionName);

  // ── Enrollment by division (all) ────────────────────────────────────────
  function divisionChartPoints(divs: import("../../types").DivisionEnrollmentTrend[]): { points: ChartPoint[]; labels: string[] } {
    const labels = divs.map((d) => d.divisionName);
    const points: ChartPoint[] = data!.years.map((year) => {
      const pt: ChartPoint = { year };
      for (const div of divs) {
        const entry = div.data.find((d) => d.year === year);
        pt[div.divisionName] = entry?.count ?? 0;
      }
      return pt;
    });
    return { points, labels };
  }

  const xAxisProps = {
    dataKey: "year" as const,
    type: "number" as const,
    domain: ["dataMin", "dataMax"] as ["dataMin", "dataMax"],
    tickFormatter: (v: number) => String(v),
  };

  function MultiLineChart({
    chartData,
    lines,
    colors = TERM_COLORS,
    yLabel,
  }: {
    chartData: ChartPoint[];
    lines: string[];
    colors?: string[];
    yLabel?: string;
  }) {
    if (!chartData.length || !lines.length) return <EmptyChart />;
    return (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
          <XAxis {...xAxisProps} />
          <YAxis label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", offset: -4, style: { fontSize: 11 } } : undefined} />
          <Tooltip labelFormatter={(v) => `Year: ${v}`} />
          <Legend />
          {lines.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <div>
      <PageHeader title="Trend Reporting" />

      <div className="p-6 lg:p-8">
        {!hasData ? (
          <div className="camp-card p-8 text-center text-stone-500">
            No enrollment data found. Import camper data to see trends.
          </div>
        ) : (
          <>
            {/* Tab bar */}
            <div className="flex gap-1 mb-8 border-b border-stone-200">
              {(["enrollment", "acs", "difficulty"] as TabId[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                    activeTab === tab
                      ? "border-b-2 border-forest-600 text-forest-700 -mb-px"
                      : "text-stone-500 hover:text-stone-700"
                  }`}
                >
                  {tab === "enrollment" ? "Enrollment" : tab === "acs" ? "ACs & Honors" : "AC Difficulty"}
                </button>
              ))}
            </div>

            {/* ── Enrollment tab ── */}
            {activeTab === "enrollment" && (
              <div>
                <SectionHeader title="Enrollment Over Time" />

                <ChartCard title="All Campers">
                  <MultiLineChart
                    chartData={enrollmentAllData}
                    lines={["All Campers"]}
                    colors={["#166534"]}
                  />
                </ChartCard>

                {termLabels.length > 0 && (
                  <ChartCard title="Campers by Term">
                    <MultiLineChart
                      chartData={termEnrollmentData}
                      lines={termLabels}
                      colors={TERM_COLORS}
                    />
                  </ChartCard>
                )}

                {divisionEnrollmentLabels.length > 0 && (
                  <>
                    <SectionHeader title="Enrollment by Division" />

                    {(() => {
                      const { points, labels } = divisionChartPoints(data.enrollment.byDivision);
                      return (
                        <ChartCard title="All Campers by Division">
                          <MultiLineChart chartData={points} lines={labels} colors={CABIN_COLORS} />
                        </ChartCard>
                      );
                    })()}

                    {data.enrollment.byTermDivision.map((td, idx) => {
                      const { points, labels } = divisionChartPoints(td.divisions);
                      const shortLabel = extractTermLabel(td.termLabel, td.termPosition);
                      return (
                        <ChartCard key={idx} title={`${shortLabel} Campers by Division`}>
                          <MultiLineChart chartData={points} lines={labels} colors={CABIN_COLORS} />
                        </ChartCard>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {/* ── ACs & Honors tab ── */}
            {activeTab === "acs" && (
              <div>
                <SectionHeader title="AC Trends Over Time" />

                <ChartCard title="Total ACs — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("totalACs")}
                    lines={["All Campers"]}
                    colors={["#166534"]}
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="Total ACs — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("totalACs").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="Total ACs — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("totalACs").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                    />
                  </ChartCard>
                )}

                <ChartCard title="ACs per Camper — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("acsPerCamper")}
                    lines={["All Campers"]}
                    colors={["#166534"]}
                    yLabel="avg"
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="ACs per Camper — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("acsPerCamper").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="ACs per Camper — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("acsPerCamper").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}

                <SectionHeader title="Honor Trends Over Time" />

                <ChartCard title="Total Honors — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("totalHonors")}
                    lines={["All Campers"]}
                    colors={["#92400e"]}
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="Total Honors — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("totalHonors").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="Total Honors — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("totalHonors").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                    />
                  </ChartCard>
                )}

                <ChartCard title="Honors per Camper — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("honorsPerCamper")}
                    lines={["All Campers"]}
                    colors={["#92400e"]}
                    yLabel="avg"
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="Honors per Camper — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("honorsPerCamper").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="Honors per Camper — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("honorsPerCamper").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}

                <SectionHeader title="Graduation Trends Over Time" />

                <ChartCard title="Total Graduations — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("totalGraduations")}
                    lines={["All Campers"]}
                    colors={["#1e40af"]}
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="Total Graduations — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("totalGraduations").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="Total Graduations — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("totalGraduations").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                    />
                  </ChartCard>
                )}

                <ChartCard title="Graduations per Camper — All Campers">
                  <MultiLineChart
                    chartData={buildAcAllData("graduationsPerCamper")}
                    lines={["All Campers"]}
                    colors={["#1e40af"]}
                    yLabel="avg"
                  />
                </ChartCard>

                {acTermLabels.length > 0 && (
                  <ChartCard title="Graduations per Camper — by Term">
                    <MultiLineChart
                      chartData={buildAcTermData("graduationsPerCamper").points}
                      lines={acTermLabels}
                      colors={TERM_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}

                {acDivisionLabels.length > 0 && (
                  <ChartCard title="Graduations per Camper — by Division">
                    <MultiLineChart
                      chartData={buildAcDivisionData("graduationsPerCamper").points}
                      lines={acDivisionLabels}
                      colors={CABIN_COLORS}
                      yLabel="avg"
                    />
                  </ChartCard>
                )}
              </div>
            )}

            {/* ── AC Difficulty tab ── */}
            {activeTab === "difficulty" && (
              <div>
                {rankingsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <DifficultyPanel
                    lifetime={lifetimeRankings}
                    term={termRankings}
                    termLabel={selectedTerm?.name ?? `${selectedYear}`}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── AC Difficulty Panel ──────────────────────────────────────────────────────

type RankRow = {
  key: string;
  primary: string;
  secondary?: string;
  completed: number;
  denominator: number;
  rate: number;
};

type EligibleRow = RankRow & { ratio: number };

function formatRate(completed: number, denominator: number): string {
  if (denominator === 0) return "0%";
  const pct = (completed / denominator) * 100;
  if (pct === 0) return "0%";
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

function sortByRatio(rows: EligibleRow[], direction: "desc" | "asc"): RankRow[] {
  const sign = direction === "desc" ? -1 : 1;
  return [...rows]
    .sort((a, b) => sign * (a.ratio - b.ratio) || a.primary.localeCompare(b.primary))
    .slice(0, TOP_N);
}

function rankAcs(rows: AcDifficultyRankings["acRankings"], minSample: number): { easiest: RankRow[]; hardest: RankRow[] } {
  const eligible: EligibleRow[] = rows
    .filter((r) => r.denominator >= minSample)
    .map((r) => ({
      key: `ac-${r.acId}`,
      primary: `${r.acDisplayNumber}. ${r.acName}`,
      secondary: r.activityName,
      completed: r.completedCount,
      denominator: r.denominator,
      rate: r.rate,
      ratio: r.completedCount / r.denominator,
    }));
  return { easiest: sortByRatio(eligible, "desc"), hardest: sortByRatio(eligible, "asc") };
}

function rankGraduations(rows: AcDifficultyRankings["graduationRankings"], minSample: number): { easiest: RankRow[]; hardest: RankRow[] } {
  const eligible: EligibleRow[] = rows
    .filter((r) => r.denominator >= minSample)
    .map((r) => ({
      key: `grad-${r.activityId}`,
      primary: r.activityName,
      completed: r.graduatedCount,
      denominator: r.denominator,
      rate: r.rate,
      ratio: r.graduatedCount / r.denominator,
    }));
  return { easiest: sortByRatio(eligible, "desc"), hardest: sortByRatio(eligible, "asc") };
}

function RankingTable({ title, rows, denomLabel }: { title: string; rows: RankRow[]; denomLabel: string }) {
  return (
    <div className="camp-card p-4">
      <h4 className="text-sm font-semibold text-stone-700 mb-3">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-stone-400 text-xs">Not enough data ({denomLabel}).</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-stone-400 text-left">
              <th className="py-1 pr-2 font-medium w-6">#</th>
              <th className="py-1 pr-2 font-medium">Name</th>
              <th className="py-1 pr-2 font-medium text-right">Completed</th>
              <th className="py-1 font-medium text-right">Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((r, idx) => (
              <tr key={r.key} className="text-stone-700">
                <td className="py-1.5 pr-2 tabular-nums text-stone-400">{idx + 1}</td>
                <td className="py-1.5 pr-2">
                  <div className="font-medium">{r.primary}</div>
                  {r.secondary && <div className="text-stone-400 text-[11px]">{r.secondary}</div>}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{r.completed} / {r.denominator}</td>
                <td className="py-1.5 text-right tabular-nums font-semibold">{formatRate(r.completed, r.denominator)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ScopeSection({
  scopeLabel,
  data,
}: {
  scopeLabel: string;
  data: AcDifficultyRankings | null;
}) {
  if (!data) return null;
  const acs = rankAcs(data.acRankings, MIN_SAMPLE);
  const grads = rankGraduations(data.graduationRankings, MIN_SAMPLE);
  const denomLabel = `${data.denominator} campers`;
  return (
    <div>
      <h3 className="text-base font-semibold text-stone-800 mb-1">{scopeLabel}</h3>
      <p className="text-xs text-stone-500 mb-4">
        Denominator: {denomLabel}. Showing top {TOP_N}; minimum {MIN_SAMPLE} campers required.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <RankingTable title={`Easiest ACs — ${scopeLabel}`} rows={acs.easiest} denomLabel={denomLabel} />
        <RankingTable title={`Hardest ACs — ${scopeLabel}`} rows={acs.hardest} denomLabel={denomLabel} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <RankingTable title={`Easiest to Graduate — ${scopeLabel}`} rows={grads.easiest} denomLabel={denomLabel} />
        <RankingTable title={`Hardest to Graduate — ${scopeLabel}`} rows={grads.hardest} denomLabel={denomLabel} />
      </div>
    </div>
  );
}

function DifficultyPanel({
  lifetime,
  term,
  termLabel,
}: {
  lifetime: AcDifficultyRankings | null;
  term: AcDifficultyRankings | null;
  termLabel: string;
}) {
  return (
    <div>
      <ScopeSection scopeLabel="Lifetime" data={lifetime} />
      <ScopeSection scopeLabel={termLabel} data={term} />
    </div>
  );
}
